import JSZip from "jszip";
import { toast } from "@/components/ui/sonner";
import { imageSrc } from "@/lib/api";

export function lbToast(msg) {
  toast(msg, {
    duration: 2500,
    style: { background: "#141414", color: "#EDEDED", borderLeft: "3px solid #F97316", borderRadius: "8px" },
    className: "font-body",
  });
}

export async function copyText(value, label = "Copied") {
  const text = value || "";
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
    } else {
      legacyCopy(text);
    }
    lbToast(label);
  } catch {
    try {
      legacyCopy(text);
      lbToast(label);
    } catch {
      toast.error("Could not copy");
    }
  }
}

export function copyCaption(text) {
  return copyText(text || "", "Caption copied");
}

function legacyCopy(value) {
  const ta = document.createElement("textarea");
  ta.value = value;
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.focus();
  ta.select();
  const ok = document.execCommand("copy");
  document.body.removeChild(ta);
  if (!ok) throw new Error("execCommand copy failed");
}

function slug(s) {
  return (s || "asset")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40) || "asset";
}

async function fetchBlob(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`fetch failed: ${res.status}`);
  return res.blob();
}

function blobExt(blob) {
  return (blob.type && blob.type.split("/")[1]) || "png";
}

function triggerDownload(blob, filename) {
  const obj = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = obj;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(obj);
}

// Single asset group: one slide downloads directly, multiple slides bundle into one .zip
export async function exportGroup(group) {
  const items = (group.items || []).filter((it) => it.url);
  if (items.length === 0) {
    toast.error("Nothing to export yet");
    return;
  }
  const base = slug(group.title || group.assetType);
  try {
    if (items.length === 1) {
      const blob = await fetchBlob(imageSrc(items[0].url));
      triggerDownload(blob, `${base}.${blobExt(blob)}`);
      lbToast("Image exported");
      return;
    }
    const zip = new JSZip();
    for (let i = 0; i < items.length; i++) {
      // eslint-disable-next-line no-await-in-loop
      const blob = await fetchBlob(imageSrc(items[i].url));
      zip.file(`${base}-slide-${i + 1}.${blobExt(blob)}`, blob);
    }
    const out = await zip.generateAsync({ type: "blob" });
    triggerDownload(out, `${base}.zip`);
    lbToast(`Downloaded ${items.length} slides (zip)`);
  } catch {
    toast.error("Could not export image");
  }
}

// Many groups ("Download all"): bundle every slide across all assets into one .zip, foldered per asset
export async function exportGroups(groups) {
  const valid = (groups || []).filter((g) => (g.items || []).some((it) => it.url));
  if (valid.length === 0) {
    toast.error("Nothing to export yet");
    return;
  }
  if (valid.length === 1) return exportGroup(valid[0]);
  lbToast("Preparing your download...");
  try {
    const zip = new JSZip();
    const used = {};
    let total = 0;
    for (const g of valid) {
      let base = slug(g.title || g.assetType);
      used[base] = (used[base] || 0) + 1;
      if (used[base] > 1) base = `${base}-${used[base]}`;
      const items = (g.items || []).filter((it) => it.url);
      const folder = zip.folder(base);
      for (let i = 0; i < items.length; i++) {
        // eslint-disable-next-line no-await-in-loop
        const blob = await fetchBlob(imageSrc(items[i].url));
        const name = items.length > 1 ? `slide-${i + 1}` : base;
        folder.file(`${name}.${blobExt(blob)}`, blob);
        total += 1;
      }
    }
    const out = await zip.generateAsync({ type: "blob" });
    triggerDownload(out, "hdg-assets.zip");
    lbToast(`Downloaded ${total} images across ${valid.length} assets (zip)`);
  } catch {
    toast.error("Could not export assets");
  }
}
